import React, { useState } from "react";
import { LifeBuoy, X } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";

const CATEGORIES = [
  "Can't watch the stream",
  "Audio/video quality",
  "Can't find a session",
  "Can't ask a question",
  "Login / account issue",
  "Something else",
];

/**
 * Persistent floating "Need help?" button — bottom-right.
 * Renders only for logged-in users (logged-out viewers don't have an account to support).
 */
export default function SupportFab() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  if (!user) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (message.trim().length < 4) {
      toast.error("Please describe the issue a little more.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/support/tickets", {
        message: message.trim(),
        category: category || null,
        page_url: typeof window !== "undefined" ? window.location.href : null,
        user_agent: typeof navigator !== "undefined" ? navigator.userAgent : null,
        session_context: null,
      });
      setSent(true);
      setMessage("");
      setCategory("");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const close = () => {
    setOpen(false);
    // Reset the "thanks" state after the panel closes so a second report starts clean.
    setTimeout(() => setSent(false), 250);
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Get help"
        data-testid="support-fab-btn"
        className="fixed bottom-5 right-5 z-40 inline-flex items-center gap-2 rounded-full bg-slate-900 text-white px-4 py-3 shadow-xl shadow-slate-900/20 hover:bg-slate-800 transition-colors"
      >
        <LifeBuoy size={17} />
        <span className="text-sm font-semibold hidden sm:inline">Need help?</span>
      </button>

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-end sm:justify-end p-0 sm:p-6 bg-black/30 backdrop-blur-sm"
          onClick={close}
          data-testid="support-fab-overlay"
        >
          <div
            className="w-full sm:w-[420px] bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl p-6 sm:p-7 max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            data-testid="support-fab-panel"
          >
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-[#28BBE3]/10 flex items-center justify-center">
                  <LifeBuoy size={17} className="text-[#28BBE3]" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-[0.22em] text-slate-500 font-semibold">Support</div>
                  <h2 className="font-heading text-xl tracking-tight text-slate-900 font-medium">
                    {sent ? "Thanks — we're on it." : "Tell us what's wrong."}
                  </h2>
                </div>
              </div>
              <button
                type="button"
                onClick={close}
                className="text-slate-400 hover:text-slate-700 p-1"
                aria-label="Close"
                data-testid="support-fab-close-btn"
              >
                <X size={18} />
              </button>
            </div>

            {sent ? (
              <div className="mt-5 space-y-3">
                <p className="text-sm text-slate-600 leading-relaxed">
                  We've got your message and we'll get back to you at{" "}
                  <span className="font-semibold text-slate-900">{user.email}</span> as soon as we can.
                </p>
                <p className="text-xs text-slate-400">
                  Urgent? Email <a href="mailto:tessa@tessardavis.com" className="text-[#28BBE3] hover:underline">tessa@tessardavis.com</a>.
                </p>
                <div className="pt-2 flex gap-2">
                  <Button onClick={() => setSent(false)} variant="outline" className="rounded-full border-slate-300" data-testid="support-fab-new-btn">
                    Report another issue
                  </Button>
                  <Button onClick={close} className="rounded-full bg-slate-900 hover:bg-slate-800 text-white" data-testid="support-fab-done-btn">
                    Done
                  </Button>
                </div>
              </div>
            ) : (
              <form onSubmit={submit} className="mt-5 space-y-3" data-testid="support-fab-form">
                <p className="text-xs text-slate-500 leading-relaxed">
                  We'll automatically include your email, the page you're on, and your browser — you don't need to type those.
                </p>
                <div>
                  <label className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">What were you trying to do?</label>
                  <select
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    data-testid="support-fab-category-select"
                    className="mt-1.5 w-full rounded-2xl border border-slate-200 bg-white px-3 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#28BBE3]/40"
                  >
                    <option value="">Optional…</option>
                    {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">Describe the issue</label>
                  <textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    rows={4}
                    required
                    minLength={4}
                    maxLength={2000}
                    placeholder="e.g. The video for Room 2 keeps freezing every few seconds…"
                    data-testid="support-fab-message-input"
                    className="mt-1.5 w-full rounded-2xl border border-slate-200 bg-white px-3 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#28BBE3]/40 resize-none"
                  />
                  <div className="mt-1 text-right text-[11px] text-slate-400">{message.length}/2000</div>
                </div>
                <div className="flex items-center gap-2 justify-end pt-1">
                  <Button type="button" variant="ghost" onClick={close} className="rounded-full text-slate-600" data-testid="support-fab-cancel-btn">
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={submitting}
                    className="rounded-full bg-[#28BBE3] hover:bg-[#1FA8CC] text-white"
                    data-testid="support-fab-submit-btn"
                  >
                    {submitting ? "Sending…" : "Send"}
                  </Button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
}
